import React, { Fragment, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Logo from '../../assets/logo-sm.svg';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { Redirect, useLocation } from 'react-router';
import { verifyMfa, resetMfaError, reset } from '../../slices/userSlice';
import SubmitButton from '../../components/submitButton';
import { Notification } from '../../components/notifications';
import { Role } from '../../common/constants';
import { Link } from 'react-router-dom';

type MfaLocationState = {
  mfaToken: string;
  mfaSetupRequired: boolean;
  qrCodeUri?: string;
  manualKey?: string;
};

const MfaVerify = () => {
  const dispatch = useAppDispatch();
  const { state } = useLocation<MfaLocationState>();

  const [code, setCode] = useState('');
  const [showManualKey, setShowManualKey] = useState(false);

  // Selectors — must be called before any conditional returns
  const hasError = useAppSelector((s) => s.user.status === 'failed');
  const errorMessage = useAppSelector((s) =>
    s.user.status === 'failed'
      ? s.user.statusMessage ?? 'Verification failed. Please try again.'
      : ''
  );
  const pendingVerify = useAppSelector((s) => s.user.status === 'verifying_mfa');
  const isLoggedIn = useAppSelector((s) => s.user.status === 'authenticated');
  const userRoles = useAppSelector((s) => s.user.roles);
  const isPasswordAboutToExpire = useAppSelector(
    (s) => s.user.passwordAboutToExpire
  );

  // If no MFA state was passed, redirect to login
  if (!state?.mfaToken) {
    return <Redirect to='/login' />;
  }

  const { mfaToken, mfaSetupRequired, qrCodeUri, manualKey } = state;

  if (isLoggedIn) {
    if (isPasswordAboutToExpire)
      return <Redirect to='/forced-change-password' />;

    if (userRoles?.includes(Role.Admin))
      return <Redirect to='/admin/therapists' />;

    if (userRoles?.includes(Role.BillingSpecialist))
      return <Redirect to='/proofing' />;

    if (userRoles?.includes(Role.Therapist))
      return <Redirect to='/' />;

    return <Redirect to='/' />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    dispatch(verifyMfa({ mfaToken, code }));
  };

  const closeErrors = () => {
    // Dismiss error but keep MFA state so user stays on this page
    dispatch(resetMfaError());
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  return (
    <div
      className='flex bg-ptsblue-normal flex-col w-full items-center justify-center'
      style={{ height: '100vh' }}
    >
      <div className='text-gray-900 w-full p-6 md:max-w-xl'>
        <form
          className='bg-white rounded-lg border shadow-lg p-10 mb-2'
          onSubmit={handleSubmit}
        >
          <img
            src={Logo}
            alt='Pediatric Therapeutic Services'
            className='object-contain object-center h-28 w-full mb-6'
          />

          <h2 className='text-xl font-semibold text-center mb-2'>
            {mfaSetupRequired
              ? 'Set Up Two-Factor Authentication'
              : 'Two-Factor Authentication'}
          </h2>

          <p className='text-sm text-gray-600 text-center mb-6'>
            {mfaSetupRequired
              ? 'Scan the QR code below with your authenticator app (e.g., Google Authenticator, Microsoft Authenticator), then enter the 6-digit code.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>

          {hasError && (
            <Notification
              isSuccess={false}
              isOpen={hasError}
              onClose={closeErrors}
            >
              <Fragment>
                {errorMessage}
                {errorMessage.includes('expired') && (
                  <React.Fragment>
                    &nbsp;
                    <Link
                      className='block text-sm text-white underline mt-1'
                      to='/login'
                    >
                      Return to login
                    </Link>
                  </React.Fragment>
                )}
              </Fragment>
            </Notification>
          )}

          {mfaSetupRequired && qrCodeUri && (
            <div className='mb-6'>
              <div className='flex justify-center mb-4'>
                <div className='p-4 bg-white border rounded-lg'>
                  <QRCodeSVG value={qrCodeUri} size={200} />
                </div>
              </div>

              <div className='text-center'>
                <button
                  type='button'
                  className='text-sm text-blue-600 underline hover:text-blue-800'
                  onClick={() => setShowManualKey(!showManualKey)}
                >
                  {showManualKey
                    ? 'Hide manual entry key'
                    : "Can't scan? Enter key manually"}
                </button>

                {showManualKey && manualKey && (
                  <div className='mt-2 p-3 bg-gray-100 rounded text-center'>
                    <p className='text-xs text-gray-500 mb-1'>
                      Manual entry key:
                    </p>
                    <code className='text-sm font-mono font-bold tracking-widest break-all'>
                      {manualKey}
                    </code>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className='mb-6'>
            <label className='block mb-2' htmlFor='mfaCode'>
              Verification Code
            </label>
            <input
              id='mfaCode'
              type='text'
              inputMode='numeric'
              autoComplete='one-time-code'
              placeholder='000000'
              value={code}
              onChange={handleCodeChange}
              maxLength={6}
              className='text-center text-2xl tracking-widest font-mono'
              autoFocus
            />
          </div>

          <div className='flex items-center justify-between'>
            <SubmitButton
              spinning={pendingVerify}
              text={mfaSetupRequired ? 'Verify & Enable' : 'Verify'}
            />
            <Link to='/login' onClick={() => dispatch(reset())}>Back to login</Link>
          </div>

          <div className='mt-6 border-t pt-4'>
            <p className='text-xs font-semibold text-gray-500 text-center mb-1'>
              Can't complete verification?
            </p>
            <p className='text-xs text-gray-400 text-center mb-3'>
              This can happen if you lost or replaced your phone, deleted your
              authenticator app, switched to a new device, or your codes are no
              longer being accepted.
            </p>
            <div className='text-center'>
              <Link
                to='/request-mfa-reset'
                className='text-sm text-blue-600 hover:underline'
              >
                Request an MFA reset
              </Link>
            </div>
          </div>
        </form>

        <p className='text-right text-gray-300 text-xs'>
          &copy;Copyright 2026 – Kelly Pediatric Therapy.
          <br />
          All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default MfaVerify;
